import { mapValues } from 'lodash/fp';
import { useMemo, VFC } from 'react';

import Accordion from 'dashboard/components/Accordion';
import EditKeyValuePairsPanel from 'dashboard/components/WorkflowControlPanel/StepConfigAction/EditKeyValuePairsPanel';
import FieldsList from 'dashboard/components/WorkflowControlPanel/StepConfigAction/FieldsList';
import OptionsFieldMappingsPanel from 'dashboard/components/WorkflowControlPanel/StepConfigAction/OptionsFieldMappingsPanel';
import MaximizablePanel from 'dashboard/components-lib/MaximizablePanel';
import { getHandlebars } from 'dashboard/lib/utils/handlebars';
import {
  filterUsableObjects,
  generateJsonSchemaConfig,
  UsableObjectConfig,
} from 'dashboard/lib/workflows';
import { ObjectPropertiesSchema } from 'dashboard/models/external-integration';
import { WorkflowError, WorkflowFieldObjectKey } from 'dashboard/models/workflow';
import { WorkflowDynamicFlowFieldsReferenceAction } from 'dashboard/models/workflow-version';
import Spacer from 'design-system/components/Spacer';
import useToggle from 'lib/hooks/useToggle';
import { JsonSchemaObject, JsonSchemaTypes } from 'lib/json-schema';

const INQUIRY_FIELD_PREFIX = 'trigger.fields.';
const INQUIRY_DISPLAY_PREFIX = 'inquiry.';

interface ParamsLegacyDynamicFlowUpdateFieldsProps {
  title: string;
  fieldsParamKey: string;
  fieldMapping: Record<string, string>;
  usableObjects: UsableObjectConfig[];
  permittedObjects?: WorkflowFieldObjectKey[];
  mapsFromInquiryFields?: boolean;
  errors: WorkflowError[];
  editable: boolean;
  updateFieldMapping: (
    newParams: Partial<WorkflowDynamicFlowFieldsReferenceAction['params']>,
  ) => void;
  panelTitle?: string | null;
  leftFieldOptions?: ObjectPropertiesSchema[];
  verificationStepToken?: string | null;
}

const ParamsLegacyDynamicFlowUpdateFields: VFC<ParamsLegacyDynamicFlowUpdateFieldsProps> = ({
  title,
  fieldsParamKey,
  fieldMapping,
  usableObjects,
  permittedObjects = [WorkflowFieldObjectKey.Inquiry],
  mapsFromInquiryFields = true,
  errors,
  editable,
  panelTitle = null,
  leftFieldOptions = [],
  verificationStepToken,
  updateFieldMapping,
}: ParamsLegacyDynamicFlowUpdateFieldsProps) => {
  const [showFields, { toggle: toggleFields, disable: closeFields }] = useToggle(false);

  // for source object
  const { jsonSchema, tokensToDisplayValue } = useMemo(() => {
    const filteredObjects = filterUsableObjects({
      usableObjects,
      options: { permittedObjects },
    });
    return generateJsonSchemaConfig({
      usableObjects: filteredObjects,
    });
  }, [usableObjects, permittedObjects]);

  let displayValuePrefix = INQUIRY_DISPLAY_PREFIX;
  let valuePrefix = INQUIRY_FIELD_PREFIX;
  let processedJsonSchema: JsonSchemaObject = { type: JsonSchemaTypes.Object, properties: {} };
  if (!mapsFromInquiryFields && verificationStepToken) {
    displayValuePrefix = tokensToDisplayValue[verificationStepToken] + '.';
    valuePrefix = verificationStepToken + '.';
    processedJsonSchema = jsonSchema;
  } else {
    processedJsonSchema = {
      type: JsonSchemaTypes.Object,
      properties: {
        inquiry: {
          type: JsonSchemaTypes.Object,
          description: 'Inquiry Fields',
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          properties: jsonSchema?.properties.trigger.properties.fields.properties,
        },
      },
    };
  }

  const handleChangeFieldMappings = (keyValueObject: Record<string, string>) => {
    const fields: Record<string, string> = {};
    for (const key in keyValueObject) {
      const value = keyValueObject[key];
      const handleBarsMatch = getHandlebars(value);
      /**
       *  when mapping to inquiry fields from verification, the display prefix is added in keyValueObject,
       *  but the verification token is returned when selecting a value from the dropdown, so both
       *  need to be stripped before updating the field mapping value.
       *
       *  Also, the last handleBarsMatch value is used here because the KVP panel appends rather than
       *  replacing when the existing value is not explicitly removed prior to selecting a new value.
       */
      fields[key] = handleBarsMatch[handleBarsMatch.length - 1]
        ?.replace(displayValuePrefix, '')
        ?.replace(valuePrefix, '');
    }
    updateFieldMapping({
      [fieldsParamKey]: fields,
    });
  };

  /**
   * Wrap params mapping in handlebars and prefix 'trigger.fields'
   * OR 'run_verification_[type]' if updating inquiry from verification.
   *
   * The action itself directly accesses field attributes in params,
   * but we prefix the object path here and strip before updating to
   * comply with handlebar validations
   */
  const keyValueObject = useMemo(() => {
    return mapValues((value) => `{{${displayValuePrefix}${value}}}`, fieldMapping);
  }, [fieldMapping, displayValuePrefix]);

  return (
    <>
      <Spacer height={4} />
      <Accordion defaultExpanded title={title}>
        <Spacer height={2} />
        {showFields &&
          (leftFieldOptions.length > 0 ? (
            <OptionsFieldMappingsPanel
              editable={editable}
              keyValueObject={keyValueObject}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              title={panelTitle}
              idPrefix='edit-key-value-panel__'
              updateObject={handleChangeFieldMappings}
              jsonSchema={processedJsonSchema}
              tokensToDisplayValue={tokensToDisplayValue}
              options={leftFieldOptions}
              onOutsideClick={closeFields}
            />
          ) : (
            <MaximizablePanel
              outsideExceptionalIdPrefixes={['edit-key-value-panel__']}
              title={title}
              onOutsideClickAndEscPress={closeFields}
            >
              <EditKeyValuePairsPanel
                editable={editable}
                keyValueObject={keyValueObject}
                updateObject={handleChangeFieldMappings}
                jsonSchema={processedJsonSchema}
                tokensToDisplayValue={tokensToDisplayValue}
              />
            </MaximizablePanel>
          ))}
        {fieldMapping && (
          <FieldsList
            fields={fieldMapping}
            fieldKey={fieldsParamKey}
            editable={editable}
            toggleEditPanel={toggleFields}
            tokensToDisplayValue={tokensToDisplayValue}
            errors={errors}
          />
        )}
      </Accordion>
    </>
  );
};

export default ParamsLegacyDynamicFlowUpdateFields;
